import { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 96;
const CONNECT_DISTANCE = 1.65;
const PETROL = 0x005677;
const TEAL = 0x3ac1cd;

/**
 * WebGL particle network for the sign-in brand panel.
 * Lazy-loads Three.js and respects prefers-reduced-motion.
 */
export function SignInScene() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    let disposed = false;
    let animationId = 0;
    let renderer: import('three').WebGLRenderer | null = null;

    const onPointerMove = (e: PointerEvent) => {
      if (!container.isConnected) return;
      const rect = container.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      container.dataset.px = String(nx * 0.35);
      container.dataset.py = String(ny * 0.25);
    };

    const init = async () => {
      const THREE = await import('three');
      if (disposed || !containerRef.current) return;

      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width === 0 || height === 0) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
      camera.position.z = 5.5;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(width, height);
      renderer.setClearColor(0x000000, 0);
      container.appendChild(renderer.domElement);

      const group = new THREE.Group();
      scene.add(group);

      const positions = new Float32Array(PARTICLE_COUNT * 3);
      const velocities = new Float32Array(PARTICLE_COUNT * 3);
      const spread = 3.2;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const i3 = i * 3;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const r = spread * (0.35 + Math.random() * 0.65);
        positions[i3] = r * Math.sin(phi) * Math.cos(theta);
        positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        positions[i3 + 2] = r * Math.cos(phi) * 0.6;
        velocities[i3] = (Math.random() - 0.5) * 0.004;
        velocities[i3 + 1] = (Math.random() - 0.5) * 0.004;
        velocities[i3 + 2] = (Math.random() - 0.5) * 0.003;
      }

      const pointsGeo = new THREE.BufferGeometry();
      pointsGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const pointsMat = new THREE.PointsMaterial({
        color: TEAL,
        size: 0.055,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const points = new THREE.Points(pointsGeo, pointsMat);
      group.add(points);

      const linePositions: number[] = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        for (let j = i + 1; j < PARTICLE_COUNT; j++) {
          const dx = positions[i * 3] - positions[j * 3];
          const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
          const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
          if (Math.hypot(dx, dy, dz) < CONNECT_DISTANCE) {
            linePositions.push(
              positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
              positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2],
            );
          }
        }
      }

      const linesGeo = new THREE.BufferGeometry();
      linesGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      const linesMat = new THREE.LineBasicMaterial({
        color: PETROL,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const lines = new THREE.LineSegments(linesGeo, linesMat);
      group.add(lines);

      const ringGeo = new THREE.TorusGeometry(2.1, 0.012, 8, 128);
      const ringMat = new THREE.MeshBasicMaterial({
        color: TEAL,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = Math.PI * 0.42;
      group.add(ring);

      const innerRing = new THREE.Mesh(
        new THREE.TorusGeometry(1.35, 0.008, 8, 96),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.06,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      innerRing.rotation.x = Math.PI * 0.55;
      innerRing.rotation.y = Math.PI * 0.2;
      group.add(innerRing);

      const clock = new THREE.Clock();
      container.dataset.px = '0';
      container.dataset.py = '0';
      window.addEventListener('pointermove', onPointerMove, { passive: true });

      const resizeObserver = new ResizeObserver(() => {
        if (!renderer || !container.isConnected) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w === 0 || h === 0) return;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      });
      resizeObserver.observe(container);

      const animate = () => {
        if (disposed) return;
        animationId = requestAnimationFrame(animate);

        const t = clock.getElapsedTime();
        const posAttr = pointsGeo.attributes.position;
        const posArr = posAttr.array as Float32Array;

        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const i3 = i * 3;
          posArr[i3] += velocities[i3];
          posArr[i3 + 1] += velocities[i3 + 1];
          posArr[i3 + 2] += velocities[i3 + 2];

          const dist = Math.hypot(posArr[i3], posArr[i3 + 1], posArr[i3 + 2]);
          if (dist > spread || dist < 0.4) {
            velocities[i3] *= -1;
            velocities[i3 + 1] *= -1;
            velocities[i3 + 2] *= -1;
          }
        }
        posAttr.needsUpdate = true;

        const px = parseFloat(container.dataset.px ?? '0');
        const py = parseFloat(container.dataset.py ?? '0');
        group.rotation.y = t * 0.08 + px;
        group.rotation.x = t * 0.04 + py;
        ring.rotation.z = t * 0.12;
        innerRing.rotation.z = -t * 0.09;

        renderer!.render(scene, camera);
      };

      animate();

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('pointermove', onPointerMove);
        pointsGeo.dispose();
        pointsMat.dispose();
        linesGeo.dispose();
        linesMat.dispose();
        ringGeo.dispose();
        ringMat.dispose();
        innerRing.geometry.dispose();
        (innerRing.material as import('three').Material).dispose();
        renderer?.dispose();
        if (renderer?.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      };
    };

    let cleanupScene: (() => void) | undefined;
    init().then((cleanup) => {
      cleanupScene = cleanup;
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(animationId);
      cleanupScene?.();
      renderer?.dispose();
      if (renderer?.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      renderer = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
    />
  );
}
